import { ArrowLeft, FolderOpen, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import AppHeader from "@/components/AppHeader";

export default function HistoryPage() {
  const navigate = useNavigate();
  const [rounds, setRounds] = useState([]);
  const [filters, setFilters] = useState({
    query: "",
    tournament: "",
    team: "",
    date_from: "",
    date_to: "",
  });
  const loadRounds = useCallback(async () => {
    const params = new URLSearchParams(
      Object.entries(filters).filter(([, value]) => value),
    );
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/rounds?${params}`,
      { credentials: "include" },
    );
    if (!response.ok) {
      toast.info("Round History is available with Pro.");
      return;
    }
    setRounds(await response.json());
  }, [filters]);
  useEffect(() => {
    loadRounds();
  }, [loadRounds]);
  const remove = async (id) => {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/rounds/${id}`,
      { method: "DELETE", credentials: "include" },
    );
    if (response.ok) {
      setRounds((current) => current.filter((round) => round.id !== id));
      toast.success("Saved round deleted.");
    }
  };
  const loadRound = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/rounds/${id}`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Saved round could not be opened.");
      const round = await response.json();
      sessionStorage.setItem(
        "pfjudgepro-open-round",
        JSON.stringify({
          details: {
            resolution: round.details.resolution,
            tournament: round.details.tournament,
            roundNumber: round.details.round_number,
            proTeam: round.details.pro_team,
            conTeam: round.details.con_team,
          },
          notes: round.notes,
          proScores: round.pro_scores,
          conScores: round.con_scores,
          speakers: round.speakers,
          bestSpeakerId: round.best_speaker_id,
          customCriteria: round.custom_criteria || [],
          timerPresetName: round.timer_preset_name,
        }),
      );
      toast.success("Saved round opened in your workspace.");
      navigate("/");
    } catch (error) {
      toast.error(error.message);
    }
  };
  return (
    <div className="judge-app" data-testid="round-history-page">
      <AppHeader onNewRound={() => {}} isPro />
      <main className="history-page">
        <Link
          className="back-link"
          to="/"
          data-testid="return-to-workspace-link"
        >
          <ArrowLeft size={17} />
          Return to workspace
        </Link>
        <div className="section-heading">
          <div>
            <p className="eyebrow">Pro archive</p>
            <h1>Round history</h1>
          </div>
        </div>
        <form
          className="history-filters"
          onSubmit={(event) => {
            event.preventDefault();
            loadRounds();
          }}
        >
          <input
            placeholder="Search resolution or team"
            value={filters.query}
            onChange={(event) =>
              setFilters({ ...filters, query: event.target.value })
            }
            data-testid="history-search-input"
          />
          <input
            placeholder="Tournament"
            value={filters.tournament}
            onChange={(event) =>
              setFilters({ ...filters, tournament: event.target.value })
            }
            data-testid="history-tournament-input"
          />
          <input
            placeholder="Team"
            value={filters.team}
            onChange={(event) =>
              setFilters({ ...filters, team: event.target.value })
            }
            data-testid="history-team-input"
          />
          <input
            type="date"
            value={filters.date_from}
            onChange={(event) =>
              setFilters({ ...filters, date_from: event.target.value })
            }
            data-testid="history-date-from-input"
          />
          <input
            type="date"
            value={filters.date_to}
            onChange={(event) =>
              setFilters({ ...filters, date_to: event.target.value })
            }
            data-testid="history-date-to-input"
          />
          <button data-testid="apply-history-filters-button">
            <Search size={17} />
            Search
          </button>
        </form>
        <div className="history-list" data-testid="history-list">
          {rounds.map((round) => (
            <article
              key={round.id}
              className="history-item"
              data-testid={`history-item-${round.id}`}
            >
              <div>
                <strong>{round.details.resolution || "Untitled round"}</strong>
                <p>
                  {round.details.tournament || "No tournament"} · Round{" "}
                  {round.details.round_number || "—"}
                </p>
                <small>
                  {round.details.pro_team} vs. {round.details.con_team}
                </small>
              </div>
              <div className="history-item-actions">
                <button
                  onClick={() => loadRound(round.id)}
                  data-testid={`load-round-${round.id}`}
                  title="Open saved round"
                >
                  <FolderOpen size={17} />
                </button>
                <button
                  onClick={() => remove(round.id)}
                  data-testid={`delete-round-${round.id}`}
                  title="Delete saved round"
                >
                  <Trash2 size={17} />
                </button>
              </div>
            </article>
          ))}
          {!rounds.length && (
            <p className="empty-history" data-testid="empty-history">
              No saved rounds match these filters.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
